import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';
import { KanbanCard, KanbanCardSkeleton } from './KanbanCard';
import type { PipelineApplication } from '@/lib/api';
import { STAGE_LABELS, STAGE_COLORS, type PipelineStage } from '@/types';

interface KanbanColumnProps {
  stage: PipelineStage;
  applications: PipelineApplication[];
  isLoading?: boolean;
  /** Render cards in compact one-line mode. */
  collapsed?: boolean;
}

export function KanbanColumn({ stage, applications, isLoading, collapsed }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
    data: {
      type: 'column',
      stage,
    },
  });

  const stageColor = STAGE_COLORS[stage];
  const stageLabel = STAGE_LABELS[stage];
  const count = applications.length;

  return (
    <div className="flex flex-col h-full w-60 flex-shrink-0">
      {/* Column Header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className={cn('w-3 h-3 rounded-full', stageColor)} />
        <h3 className="font-medium text-text-primary text-sm">{stageLabel}</h3>
        <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-white/10 text-text-secondary text-xs font-medium">
          {count}
        </span>
      </div>

      {/* Column Content - Drop Zone */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex-1 rounded-lg bg-bg-secondary p-2 min-h-[200px] overflow-y-auto',
          'shadow-[inset_0_1px_3px_rgba(0,0,0,0.35)]',
          'transition-colors duration-150',
          isOver && 'bg-emerald-500/10 ring-2 ring-emerald-500/40 ring-inset',
        )}
      >
        {isLoading ? (
          <div className="space-y-2">
            <KanbanCardSkeleton />
            <KanbanCardSkeleton />
            <KanbanCardSkeleton />
          </div>
        ) : applications.length > 0 ? (
          <SortableContext
            items={applications.map((app) => app.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {applications.map((application) => (
                <KanbanCard key={application.id} application={application} collapsed={collapsed} />
              ))}
            </div>
          </SortableContext>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-text-tertiary text-center">
              No candidates in this stage
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
